'use client'

import {
  forwardRef,
  ElementRef,
  ComponentPropsWithoutRef,
  HTMLAttributes,
} from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '../../../lib/utils'

const TabsWrapper = TabsPrimitive.Root

const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      className,
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow',
      className,
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      {...props}
    />
  )
})

TabsContent.displayName = TabsPrimitive.Content.displayName

export type TabProps = {
  defaultValue?: string
  items: {
    value: string
    label: string
    children: React.ReactNode
  }[]
  onValueChange?: (value: string) => void
} & HTMLAttributes<HTMLDivElement>

const Tabs = forwardRef<HTMLDivElement, TabProps>(
  ({ className, items, defaultValue, onValueChange, ...props }, ref) => (
    <TabsWrapper
      defaultValue={defaultValue}
      className={className}
      onValueChange={onValueChange}
    >
      <TabsList className="mb-4">
        {items.map((i) => (
          <TabsTrigger key={i.value} value={i.value}>
            {i.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {items.map((i) => (
        <TabsContent
          key={`${i.value}-content`}
          value={i.value}
          className="flex flex-col gap-4"
        >
          {i.children}
        </TabsContent>
      ))}
    </TabsWrapper>
  ),
)

Tabs.displayName = 'Tabs'

export { Tabs, TabsWrapper, TabsList, TabsTrigger, TabsContent }
